(() => {
  const STORAGE_KEY = 'alesCampaignPreviewState';
  const VALID_STATES = ['before', 'active', 'ending', 'after'];
  const money = new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  });

  let runtime;
  let campaignState = 'after';
  let campaignVisible = false;
  let campaignObserver;

  const parseBoolean = (value) => value === true || value === 'true';

  function japaneseParts(date) {
    const parts = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    return Object.fromEntries(parts.map((part) => [part.type, part.value]));
  }

  function startLabel(start) {
    const p = japaneseParts(start);
    return `${p.month}/${p.day}（${p.weekday}）${p.hour}:${p.minute}開始`;
  }

  function endLabel(end) {
    const lastMinute = new Date(end.getTime() - 60 * 1000);
    const p = japaneseParts(lastMinute);
    return `${p.month}/${p.day}（${p.weekday}）${p.hour}:${p.minute}まで`;
  }

  function isSameTokyoDate(a, b) {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(a) === formatter.format(b);
  }

  function getPreviewState() {
    if (!parseBoolean(runtime?.dataset.draft)) return null;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (VALID_STATES.includes(stored)) return stored;
    const configured = runtime?.dataset.previewMode;
    return VALID_STATES.includes(configured) ? configured : 'active';
  }

  function deriveState(now, start, end) {
    const previewState = getPreviewState();
    if (previewState) return previewState;
    if (now < start) return 'before';
    if (now >= end) return 'after';
    if (end.getTime() - now.getTime() <= 48 * 60 * 60 * 1000) return 'ending';
    return 'active';
  }

  function statusLabel(state, now, start, end) {
    if (state === 'before') return startLabel(start);
    if (state === 'after') return 'キャンペーンは終了しました';
    if (state !== 'ending') return endLabel(end);

    const preview = getPreviewState();
    const remaining = preview === 'ending' ? 36 * 60 * 60 * 1000 : Math.max(0, end.getTime() - now.getTime());
    const lastMinute = new Date(end.getTime() - 60 * 1000);
    if (!preview && isSameTokyoDate(now, lastMinute)) return `本日${japaneseParts(lastMinute).hour}:${japaneseParts(lastMinute).minute}終了`;
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    return `終了まであと${hours}時間${String(minutes).padStart(2, '0')}分`;
  }

  function refreshCampaignState() {
    if (!runtime) return;
    const start = new Date(runtime.dataset.start);
    const end = new Date(runtime.dataset.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

    const now = new Date();
    campaignState = deriveState(now, start, end);
    const draft = parseBoolean(runtime.dataset.draft);
    campaignVisible = campaignState === 'active' || campaignState === 'ending' || (draft && campaignState === 'before');

    document.querySelectorAll('[data-ales-campaign-component]').forEach((component) => {
      component.hidden = !campaignVisible;
      component.dataset.campaignState = campaignState;
    });
    document.querySelectorAll('[data-ales-campaign-status]').forEach((element) => {
      element.textContent = statusLabel(campaignState, now, start, end);
    });

    document.body.classList.toggle('ales-campaign-active', campaignVisible);
    document.querySelectorAll('[data-bundle-submit]').forEach((button) => {
      const inactive = campaignState === 'before' || campaignState === 'after';
      button.disabled = inactive;
      if (inactive) {
        button.textContent = campaignState === 'before' ? '開始時刻から購入できます' : 'キャンペーンは終了しました';
      } else {
        button.textContent = '2点をまとめてカートに追加';
      }
    });

    document.querySelectorAll('[data-preview-state]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.previewState === campaignState));
    });
  }

  function quantityFor(widget) {
    const productInfo = widget.closest('product-info') || widget.closest('.product__info-container') || document;
    const input = productInfo.querySelector('input[name="quantity"]');
    const value = Number.parseInt(input?.value || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function primaryVariantId(widget) {
    const productInfo = widget.closest('product-info') || widget.closest('.product__info-container') || document;
    return productInfo.querySelector('product-form input[name="id"]')?.value || '';
  }

  function setMessage(widget, type, message = '') {
    const error = widget.querySelector('[data-bundle-error]');
    const success = widget.querySelector('[data-bundle-success]');
    error.hidden = type !== 'error';
    success.hidden = type !== 'success';
    if (type === 'error') error.textContent = message;
    if (type === 'success') success.textContent = message;
  }

  function getVariant(data, id) {
    return data?.variants?.find((variant) => String(variant.id) === String(id));
  }

  function updatePrice(widget, data) {
    const mode = widget.dataset.mode;
    const quantity = quantityFor(widget);
    let teeVariant;
    let gloveVariant;

    if (mode === 'glove') {
      gloveVariant = getVariant(data.currentProduct, primaryVariantId(widget));
      teeVariant = getVariant(data.tee, widget.querySelector('[data-tee-variant]')?.value);
    } else {
      teeVariant = getVariant(data.tee, widget.querySelector('[data-tee-variant]')?.value);
      const gloveProduct = data.gloves.find(
        (candidate) => String(candidate.id) === String(widget.querySelector('[data-glove-product]')?.value)
      );
      gloveVariant = getVariant(gloveProduct, widget.querySelector('[data-glove-variant]')?.value);
    }

    const regular = widget.querySelector('[data-regular-total]');
    const discounted = widget.querySelector('[data-discounted-total]');
    if (!teeVariant || !gloveVariant) {
      regular.textContent = '—';
      discounted.textContent = '—';
      return;
    }

    const normalTotal = (Number(teeVariant.price) + Number(gloveVariant.price)) * quantity;
    const discountedTotal = normalTotal - Number(data.discountCents) * quantity;
    regular.textContent = money.format(normalTotal / 100);
    discounted.textContent = money.format(discountedTotal / 100);
  }

  function populateGloveProducts(widget, data) {
    const modelSelect = widget.querySelector('[data-glove-model]');
    const productSelect = widget.querySelector('[data-glove-product]');
    const variantSelect = widget.querySelector('[data-glove-variant]');
    if (!modelSelect || !productSelect || !variantSelect) return;

    modelSelect.addEventListener('change', () => {
      const products = data.gloves.filter((product) => product.model === modelSelect.value);
      productSelect.innerHTML = '<option value="">カラー／コラボモデルを選択</option>';
      products.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.id;
        option.textContent = product.title;
        productSelect.append(option);
      });
      productSelect.disabled = products.length === 0;
      variantSelect.innerHTML = '<option value="">先に商品を選択</option>';
      variantSelect.disabled = true;
      widget.querySelector('[data-bundle-product-name]').textContent = 'グローブを選択してください';
      updatePrice(widget, data);
    });

    productSelect.addEventListener('change', () => {
      const product = data.gloves.find((candidate) => String(candidate.id) === String(productSelect.value));
      variantSelect.innerHTML = '<option value="">グローブのサイズを選択</option>';
      if (!product) {
        variantSelect.disabled = true;
        updatePrice(widget, data);
        return;
      }
      product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = `${variant.size || variant.title}${variant.available ? '' : '（売り切れ）'}`;
        option.disabled = !variant.available;
        variantSelect.append(option);
      });
      variantSelect.disabled = false;
      widget.querySelector('[data-bundle-product-name]').textContent = product.title;
      const image = widget.querySelector('[data-bundle-image]');
      image.src = product.image;
      image.alt = product.title;
      updatePrice(widget, data);
    });

    variantSelect.addEventListener('change', () => updatePrice(widget, data));
  }

  function currentSelection(widget, data) {
    const mode = widget.dataset.mode;
    if (mode === 'glove') {
      const glove = getVariant(data.currentProduct, primaryVariantId(widget));
      const tee = getVariant(data.tee, widget.querySelector('[data-tee-variant]')?.value);
      return { glove, tee };
    }

    const tee = getVariant(data.tee, widget.querySelector('[data-tee-variant]')?.value);
    const product = data.gloves.find(
      (candidate) => String(candidate.id) === String(widget.querySelector('[data-glove-product]')?.value)
    );
    const glove = getVariant(product, widget.querySelector('[data-glove-variant]')?.value);
    return { glove, tee };
  }

  async function cartJson() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('カート情報を取得できませんでした。');
    return response.json();
  }

  async function refreshCartPresentation(cartComponent, productId) {
    if (!cartComponent) return false;
    const isDrawer = cartComponent.tagName === 'CART-DRAWER' && cartComponent.getSectionsToRender;
    const sectionIds = isDrawer ? cartComponent.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];
    const url = `${window.routes?.cart_url || '/cart'}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return false;
    const sections = await response.json();
    if (isDrawer) {
      cartComponent.renderContents({ id: productId, sections });
      if (cartComponent.classList.contains('is-empty')) cartComponent.classList.remove('is-empty');
    } else {
      const target = document.getElementById('cart-icon-bubble');
      const html = sections['cart-icon-bubble'];
      const source = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section') : null;
      if (!target || !source) return false;
      target.innerHTML = source.innerHTML;
    }
    return true;
  }

  function variantQuantity(cart, variantId) {
    return cart.items
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  function updateCartCount(count) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      cartLink.append(bubble);
    }
    bubble.innerHTML = `<span aria-hidden="true">${count < 100 ? count : ''}</span><span class="visually-hidden">カートに${count}個のアイテム</span>`;
  }

  async function addBundle(widget, data) {
    const button = widget.querySelector('[data-bundle-submit]');
    if (button.disabled || button.dataset.loading === 'true') return;
    setMessage(widget, '');

    const { glove, tee } = currentSelection(widget, data);
    if (!tee) {
      setMessage(widget, 'error', 'Tシャツのサイズを選択してください。');
      widget.querySelector('[data-tee-variant]')?.focus();
      return;
    }
    if (!glove) {
      setMessage(widget, 'error', 'グローブのモデル・カラー・サイズを選択してください。');
      (widget.querySelector('[data-glove-model]') || widget).focus?.();
      return;
    }
    if (!tee.available || !glove.available) {
      setMessage(widget, 'error', '選択した商品は現在購入できません。別のサイズを選択してください。');
      return;
    }

    const quantity = quantityFor(widget);
    const before = await cartJson().catch(() => null);
    const pairId = `ales-set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const cartComponent = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = {
      items: [
        {
          id: Number(glove.id),
          quantity,
          properties: { _ales_campaign_pair: pairId, _ales_campaign_role: 'glove' },
        },
        {
          id: Number(tee.id),
          quantity,
          properties: { _ales_campaign_pair: pairId, _ales_campaign_role: 'tee' },
        },
      ],
    };

    if (cartComponent?.getSectionsToRender) {
      body.sections = cartComponent.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cartComponent.setActiveElement?.(document.activeElement);
    }

    button.dataset.loading = 'true';
    button.disabled = true;
    button.textContent = '追加中…';

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'カートに追加できませんでした。');

      const addedItems = result.items || [];
      const addedGlove = addedItems.some((item) => String(item.variant_id || item.id) === String(glove.id));
      const addedTee = addedItems.some((item) => String(item.variant_id || item.id) === String(tee.id));
      if (addedItems.length && (!addedGlove || !addedTee)) {
        throw new Error('2商品の追加結果を確認できませんでした。カート内容を確認してください。');
      }

      const currentCart = await cartJson();
      updateCartCount(currentCart.item_count);
      if (cartComponent?.tagName === 'CART-DRAWER') {
        await refreshCartPresentation(cartComponent, glove.id).catch(() => false);
      }
      setMessage(widget, 'success', `${quantity}組（2商品を同数）カートに追加しました。`);
    } catch (error) {
      let message = error instanceof Error ? error.message : 'カートに追加できませんでした。';
      if (before) {
        const after = await cartJson().catch(() => null);
        if (after) {
          const gloveDelta = variantQuantity(after, glove.id) - variantQuantity(before, glove.id);
          const teeDelta = variantQuantity(after, tee.id) - variantQuantity(before, tee.id);
          if ((gloveDelta > 0) !== (teeDelta > 0)) {
            message = '片方の商品だけが追加された可能性があります。カート内容を確認し、不足している商品を追加してください。';
          }
        }
      }
      setMessage(widget, 'error', message);
    } finally {
      button.dataset.loading = 'false';
      button.disabled = campaignState === 'before' || campaignState === 'after';
      button.textContent = button.disabled
        ? campaignState === 'before'
          ? '開始時刻から購入できます'
          : 'キャンペーンは終了しました'
        : '2点をまとめてカートに追加';
    }
  }

  function initWidget(widget) {
    if (widget.dataset.initialized === 'true') return;
    const json = widget.querySelector('[data-bundle-data]')?.textContent;
    if (!json) return;

    let data;
    try {
      data = JSON.parse(json);
    } catch (error) {
      console.error('ALES campaign data could not be parsed.', error);
      return;
    }

    widget.dataset.initialized = 'true';
    const panel = widget.querySelector('[data-bundle-panel]');
    const productInfo = widget.closest('product-info') || widget.closest('.product__info-container');
    widget.querySelectorAll('input[type="radio"][name^="ales-campaign-method-"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const setMode = radio.checked && radio.value === 'set';
        panel.hidden = !setMode;
        productInfo?.classList.toggle('ales-campaign-set-mode', setMode);
        setMessage(widget, '');
        if (setMode) updatePrice(widget, data);
      });
    });

    widget.querySelector('[data-tee-variant]')?.addEventListener('change', () => updatePrice(widget, data));
    widget.addEventListener('change', (event) => {
      if (event.target.matches('[data-tee-variant], [data-glove-model], [data-glove-product], [data-glove-variant]')) {
        setMessage(widget, '');
      }
    });
    populateGloveProducts(widget, data);
    widget.querySelector('[data-bundle-submit]')?.addEventListener('click', () => addBundle(widget, data));

    const productInfoRoot = widget.closest('product-info') || document;
    productInfoRoot.addEventListener('change', () => window.requestAnimationFrame(() => updatePrice(widget, data)));
    productInfoRoot.querySelector('input[name="quantity"]')?.addEventListener('change', () => updatePrice(widget, data));
  }

  function init() {
    runtime = document.querySelector('[data-ales-campaign-runtime]');
    if (!runtime) return;

    document.querySelectorAll('[data-preview-state]').forEach((button) => {
      button.addEventListener('click', () => {
        sessionStorage.setItem(STORAGE_KEY, button.dataset.previewState);
        refreshCampaignState();
      });
    });
    document.querySelectorAll('[data-ales-campaign-widget]').forEach(initWidget);
    refreshCampaignState();
    if (!campaignObserver && document.body) {
      campaignObserver = new MutationObserver((mutations) => {
        const campaignContentAdded = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches?.('[data-ales-campaign-component]') || node.querySelector?.('[data-ales-campaign-component]'))
          )
        );
        if (!campaignContentAdded) return;
        window.requestAnimationFrame(() => {
          document.querySelectorAll('[data-ales-campaign-widget]').forEach(initWidget);
          refreshCampaignState();
        });
      });
      campaignObserver.observe(document.body, { childList: true, subtree: true });
    }
    window.setInterval(refreshCampaignState, 60 * 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-ales-campaign-widget]').forEach(initWidget);
    refreshCampaignState();
  });
})();
